import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layers, Play, Loader2, Filter, Download, AlertCircle, CheckCircle2, Info, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimplificationMapping = ({ project }) => {
  const [activeTab, setActiveTab] = useState('items');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(false);
  const [filterRelevance, setFilterRelevance] = useState('all');
  const [filterBusinessArea, setFilterBusinessArea] = useState('all');
  const [filterMandatory, setFilterMandatory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showUploadCatalog, setShowUploadCatalog] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/simplification-mapping/latest`);
      setResults(response.data.results);
    } catch (error) {
      console.log('No mapping results yet');
    } finally {
      setLoading(false);
    }
  };

  const runMapping = async () => {
    setMapping(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/simplification-mapping/run`, {});
      setResults(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setMapping(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-12 h-12 text-blue-600 animate-spin" /></div>;

  const filteredItems = results?.detailed_items?.filter(item => {
    if (filterRelevance !== 'all' && item.relevance.relevance !== filterRelevance) return false;
    if (filterBusinessArea !== 'all' && item.item.business_area !== filterBusinessArea) return false;
    if (filterMandatory === 'mandatory' && !item.item.mandatory) return false;
    if (filterMandatory === 'optional' && item.item.mandatory) return false;
    return true;
  }) || [];

  const businessAreas = [...new Set(results?.detailed_items?.map(i => i.item.business_area) || [])];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Simplification Item Mapping Engine</h2>
            <p className="text-gray-600">Map SAP simplification items to actual ECC usage and impact</p>
            
            {/* SAP System Indicator */}
            {results && results.sap_system_analyzed && results.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
                  <Package className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-purple-600">{results.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({results.sap_system_analyzed.host})</span>
                </div>
                {results.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowUploadCatalog(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
            >
              <Package className="w-5 h-5" />
              <span>Upload SIMPL Catalog</span>
            </button>
            <button onClick={runMapping} disabled={mapping} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50">
              {mapping ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Mapping...</span></> : <><Play className="w-5 h-5" /><span>Run Mapping</span></>}
            </button>
          </div>
        </div>
      </div>

      {!results && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Layers className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Mapping Results</h3>
          <p className="text-gray-600 mb-6">Run simplification mapping to analyze S/4HANA items</p>
          <button onClick={runMapping} className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2">
            <Play className="w-5 h-5" />
            <span>Start Mapping</span>
          </button>
        </div>
      )}

      {results && (
        <>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{results.summary.total_catalog_items}</div>
              <div className="text-blue-100 text-sm">Total Items</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{results.summary.relevant_items}</div>
              <div className="text-green-100 text-sm">Relevant</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{results.summary.mandatory_items}</div>
              <div className="text-red-100 text-sm">Mandatory</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{results.summary.total_effort_days}</div>
              <div className="text-orange-100 text-sm">Total Days</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200">
              {[
                { id: 'items', label: 'Simplification Items' },
                { id: 'packages', label: 'Work Packages' },
                { id: 'backlog', label: 'Backlog' },
                { id: 'narratives', label: 'AI Insights' }
              ].map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 px-6 py-3 font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'items' && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select value={filterRelevance} onChange={(e) => setFilterRelevance(e.target.value)} className="px-4 py-2 border-2 border-gray-200 rounded-lg">
                      <option value="all">All Relevance</option>
                      <option value="RELEVANT">Relevant Only</option>
                      <option value="POTENTIALLY_RELEVANT">Potentially Relevant</option>
                      <option value="NOT_RELEVANT">Not Relevant</option>
                    </select>
                    <select value={filterBusinessArea} onChange={(e) => setFilterBusinessArea(e.target.value)} className="px-4 py-2 border-2 border-gray-200 rounded-lg">
                      <option value="all">All Business Areas</option>
                      {businessAreas.map(area => <option key={area} value={area}>{area}</option>)}
                    </select>
                    <select value={filterMandatory} onChange={(e) => setFilterMandatory(e.target.value)} className="px-4 py-2 border-2 border-gray-200 rounded-lg">
                      <option value="all">All Items</option>
                      <option value="mandatory">Mandatory Only</option>
                      <option value="optional">Optional Only</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    {filteredItems.map((itemData, idx) => (
                      <div key={idx} className="border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer" onClick={() => setSelectedItem(itemData)}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-bold text-gray-900">{itemData.item.title}</h4>
                              {itemData.item.mandatory && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold">MANDATORY</span>}
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                itemData.relevance.relevance === 'RELEVANT' ? 'bg-green-100 text-green-700' :
                                itemData.relevance.relevance === 'POTENTIALLY_RELEVANT' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{itemData.relevance.relevance.replace('_', ' ')}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{itemData.item.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>ID: {itemData.item.simpl_item_id}</span>
                              <span>Component: {itemData.item.application_component}</span>
                              <span>Area: {itemData.item.business_area}</span>
                              <span className={`font-semibold ${
                                itemData.impact.effort_band === 'XL' ? 'text-red-600' :
                                itemData.impact.effort_band === 'L' ? 'text-orange-600' :
                                itemData.impact.effort_band === 'M' ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>Effort: {itemData.impact.effort_band} ({itemData.impact.effort_days}d)</span>
                            </div>
                          </div>
                        </div>
                        {itemData.relevance.relevance === 'RELEVANT' && (
                          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mt-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Evidence:</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {itemData.relevance.evidence.tcodes_used.length > 0 && (
                                <div><span className="text-gray-600">T-Codes:</span> <span className="font-semibold">{itemData.relevance.evidence.tcodes_used.join(', ')}</span></div>
                              )}
                              {itemData.relevance.evidence.tables_used.length > 0 && (
                                <div><span className="text-gray-600">Tables:</span> <span className="font-semibold">{itemData.relevance.evidence.tables_used.join(', ')}</span></div>
                              )}
                            </div>
                            {itemData.relevance.evidence.nlp_fuzzy_matches && itemData.relevance.evidence.nlp_fuzzy_matches.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-blue-300">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Info className="w-4 h-4 text-purple-600" />
                                  <p className="text-xs font-semibold text-purple-700">AI-Detected Patterns:</p>
                                </div>
                                <ul className="space-y-1">
                                  {itemData.relevance.evidence.nlp_fuzzy_matches.map((match, i) => (
                                    <li key={i} className="text-xs text-gray-700 flex items-start space-x-1">
                                      <span className="text-purple-600">•</span>
                                      <span>{match}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'packages' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {results.work_packages.map((pkg, idx) => (
                      <div key={idx} className="bg-white border-2 border-blue-200 rounded-xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h4>
                            <p className="text-sm text-gray-600 mb-3">Business Area: {pkg.business_area}</p>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              pkg.type === 'code-heavy' ? 'bg-purple-100 text-purple-700' :
                              pkg.type === 'data-heavy' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>{pkg.type.replace('-', ' ').toUpperCase()}</span>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Items:</span><span className="font-semibold">{pkg.item_count}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Total Effort:</span><span className="font-semibold">{pkg.total_effort_days} days</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Duration:</span><span className="font-semibold">{pkg.estimated_duration_window}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Lead:</span><span className="font-semibold">{pkg.lead_workstream}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'backlog' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">Simplification Backlog ({results.backlog.length} tasks)</h3>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2">
                      <Download className="w-4 h-4" />
                      <span>Export Backlog</span>
                    </button>
                  </div>
                  <div className="space-y-3">
                    {results.backlog.map((task) => (
                      <div key={task.task_id} className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              task.priority === 'P0' ? 'bg-red-100 text-red-700' :
                              task.priority === 'P1' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>{task.priority}</span>
                            <h4 className="font-semibold text-gray-900">{task.description}</h4>
                            {task.mandatory && <AlertCircle className="w-4 h-4 text-red-600" />}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>Workstream: {task.owning_workstream}</span>
                            <span>Effort: {task.effort_band} ({task.effort_days}d)</span>
                            <span>Item: {task.simpl_item_id}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'narratives' && (
                <div className="space-y-6">
                  {results.ai_narratives.map((narrative, idx) => (
                    <div key={idx} className="bg-white border-2 border-purple-200 rounded-xl p-6">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{idx + 1}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{narrative.title}</h4>
                          <p className="text-sm text-gray-600 mb-1">Item ID: {narrative.simpl_item_id}</p>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                        <h5 className="font-semibold text-gray-900 mb-2">Business Explanation</h5>
                        <p className="text-sm text-gray-700">{narrative.business_explanation}</p>
                      </div>

                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                        <h5 className="font-semibold text-gray-900 mb-3">Recommended Steps</h5>
                        <ol className="space-y-2">
                          {narrative.recommended_steps.map((step, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>

                      {narrative.risks && narrative.risks.length > 0 && (
                        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-4">
                          <h5 className="font-semibold text-gray-900 mb-3">Risks</h5>
                          <ul className="space-y-1">
                            {narrative.risks.map((risk, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start space-x-2">
                                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span>{risk}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Estimated Timeline:</span>
                        <span className="font-semibold text-blue-600">{narrative.timeline}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedItem && (
            <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
          )}

          {showUploadCatalog && (
            <UploadCatalogModal onClose={() => setShowUploadCatalog(false)} />
          )}
        </>
      )}
    </div>
  );
};

const UploadCatalogModal = ({ onClose }) => {
  const [file, setFile] = useState(null);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Upload SAP Simplification Catalog</h3>
        
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">How to Get Simplification Catalog:</h4>
          <ol className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">1.</span>
              <span>Access SAP Support Portal: <a href="https://support.sap.com/s4s" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">https://support.sap.com/s4s</a></span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">2.</span>
              <span>Navigate to "Simplification Item Catalog" for your target S/4HANA release</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">3.</span>
              <span>Download catalog as <strong>JSON</strong> or CSV</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-bold text-blue-600">4.</span>
              <span>Upload file here to customize AURA's analysis</span>
            </li>
          </ol>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors mb-6">
          <input
            type="file"
            accept=".json,.csv"
            onChange={(e) => setFile(e.target.files[0])}
            className="hidden"
            id="catalog-upload"
          />
          <label htmlFor="catalog-upload" className="cursor-pointer">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-2">
              {file ? <span className="text-blue-600 font-semibold">{file.name}</span> : <>Click to browse or drag and drop</>}
            </p>
            <p className="text-xs text-gray-500">Supported: .json, .csv (from SAP Support Portal)</p>
          </label>
        </div>

        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              <p className="font-semibold mb-2">AI-Powered Fuzzy Matching (GPT-4o)</p>
              <p>AURA will use NLP to intelligently link your custom code and processes to simplification items, even when not explicitly referenced in SAP documentation.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button onClick={onClose} className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50">Cancel</button>
          <button disabled={!file} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50">
            Upload & Process
          </button>
        </div>
      </div>
    </div>
  );
};

const ItemDetailModal = ({ item, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.item.title}</h3>
          <p className="text-sm text-gray-600">ID: {item.item.simpl_item_id} | {item.item.application_component}</p>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
          <p className="text-gray-700">{item.item.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Details</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Business Area:</span><span className="font-semibold">{item.item.business_area}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Mandatory:</span><span className="font-semibold">{item.item.mandatory ? 'Yes' : 'No'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">S/4 Release:</span><span className="font-semibold">{item.item.s4_release}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Relevance:</span><span className="font-semibold">{item.relevance.relevance}</span></div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Impact</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Effort:</span><span className="font-semibold">{item.impact.effort_band} ({item.impact.effort_days} days)</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Impacted Objects:</span><span className="font-semibold">{item.impact.number_of_impacted_objects}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Criticality:</span><span className="font-semibold">{item.impact.criticality_score}%</span></div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Impacted Objects</h4>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(item.impact.impact_objects).map(([type, objects]) => (
              objects.length > 0 && (
                <div key={type} className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 capitalize">{type}:</p>
                  <div className="space-y-1">
                    {objects.slice(0, 5).map((obj, i) => (
                      <p key={i} className="text-xs text-gray-600">{obj}</p>
                    ))}
                    {objects.length > 5 && <p className="text-xs text-gray-500">+{objects.length - 5} more</p>}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Required Actions</h4>
          <div className="flex flex-wrap gap-2">
            {item.item.required_actions.map((action, i) => (
              <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm capitalize">{action}</span>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-3">SAP Reference</h4>
          <div className="bg-gray-50 rounded-lg p-4 text-sm">
            <div className="space-y-2">
              <div><span className="text-gray-600">SAP Note:</span> <span className="font-semibold">{item.item.sap_reference.note}</span></div>
              <div><span className="text-gray-600">Doc ID:</span> <span className="font-semibold">{item.item.sap_reference.doc_id}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t">
        <button onClick={onClose} className="w-full bg-gray-600 text-white px-6 py-3 rounded-xl hover:bg-gray-700">Close</button>
      </div>
    </div>
  </div>
);

export default SimplificationMapping;
